import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {UseFormMethods} from 'react-hook-form'
import styled from 'styled-components'
import {useTheme} from 'themes'
import {WINDOW_MODE_TABLET_WIDTH} from 'consts'
import {TranslationKey, useTranslation} from 'i18n'
import {requestDataPayload} from 'services'
import {GiftShopPackageFormData, TransactionCurrencyData} from 'types'
import {showSnackbar, useDidMount} from 'utils'
import {Button, ButtonIcon, ModalLoading, Paragraph} from 'common/components'
import convertUnit from 'lib/unit'
import GiftShopSettingPackageFormPrice from './GiftShopSettingPackageFormPrice'
import GiftShopSettingPackageFormTags from './GiftShopSettingPackageFormTags'
import GiftShopSettingPackageFormDuration from './GiftShopSettingPackageFormDuration'
import GiftShopSettingPackageFormDeleteModal from './GiftShopSettingPackageFormDeleteModal'

const StyledContainer = styled.div`
  width: 100%;
  height: 100%;
  border-radius: ${convertUnit(16)};
  display: flex;
  flex-direction: row;
  background-color: ${({theme}) => theme.white_3};
  overflow-y: auto;

  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    flex-direction: column;
  }
`

const StyledSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: ${({theme}) => theme.white_1};
  padding: ${convertUnit(40)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    padding: ${convertUnit(20)};
  }
`

const StyledParagraphList = styled.ul`
  margin: 0;
`

const StyledParagraph = styled(Paragraph)`
  line-height: ${convertUnit(17)};
  text-align: justify;
  white-space: pre-line;
`

export default function GiftShopSettingPackageContentForm({
  form,
  onSubmit,
  onDelete,
  tagsDisabled,
}: {
  form: UseFormMethods<GiftShopPackageFormData>
  onSubmit: (data: GiftShopPackageFormData) => Promise<void>
  onDelete?: (id: string) => Promise<void>
  tagsDisabled?: boolean
}) {
  const {translate} = useTranslation()
  const [currencyData, setCurrencyData] = useState<TransactionCurrencyData[]>(
    [],
  )
  const theme = useTheme()
  const {setValue, watch, register} = form

  const [loading, setLoading] = useState(false)
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false)
  const values = watch()
  const {currencyName, tags} = values

  const stateInputPrice = useState('0')
  const price = stateInputPrice[0]

  const handleToggleDeleteConfirmation = useCallback(() => {
    setShowDeleteConfirmation((prev) => !prev)
  }, [])

  const descriptionPoints = useMemo(
    () =>
      ([
        'giftShop:settingPackageDescription_1',
        'giftShop:settingPackageDescription_2',
        'giftShop:settingPackageDescription_3',
        'giftShop:settingPackageDescription_4',
        'giftShop:settingPackageDescription_5',
        'giftShop:settingPackageDescription_6',
        'giftShop:settingPackageDescription_7',
        'giftShop:settingPackageDescription_8',
      ] as TranslationKey[]).map((key) => (
        <li key={key}>
          <StyledParagraph color="gray_5">{translate(key)}</StyledParagraph>
        </li>
      )),
    [translate],
  )

  const handleGetCurrencyData = useCallback(
    () =>
      requestDataPayload('giftshop_get_currency', {
        useDefaultMessage: true,
        actionType: 'fetch',
        onRequestSuccess: ({data: response, status}) => {
          const {result} = response
          if (status === 200) {
            const idx = result.findIndex((item) => item.currency === 'IDR')
            const resultIdx = idx === -1 ? 0 : idx
            setValue('currencyName', result[resultIdx].currency)
            setValue('currencyId', result[resultIdx].id)
            setCurrencyData(result)
          }
        },
      }),
    [setValue],
  )

  const handleValidate = useCallback(() => {
    if (price === '' || parseInt(price, 10) === 0) {
      showSnackbar(translate('giftShop:uploadPriceEmpty'))
      return false
    }

    if (tags.length === 0) {
      showSnackbar(translate('giftShop:settingPackageFototreeNeeded'))
      return false
    }

    return true
  }, [price, tags, translate])

  const handleSubmit = useCallback(async () => {
    const isValid = handleValidate()
    if (!isValid) return

    setLoading(true)
    await onSubmit(form.getValues())
    setLoading(false)
  }, [form, handleValidate, onSubmit])

  const handleDelete = useCallback(async () => {
    if (!onDelete) return
    setLoading(true)
    const {id} = form.getValues()
    await onDelete(id)
    setLoading(false)
  }, [form, onDelete])

  const handleRenderModalLoading = useMemo(
    () => <ModalLoading visible={loading} />,
    [loading],
  )

  const handleRenderModalDelete = useMemo(
    () => (
      <GiftShopSettingPackageFormDeleteModal
        onConfirm={handleDelete}
        toggleModal={handleToggleDeleteConfirmation}
        visible={showDeleteConfirmation}
      />
    ),
    [handleDelete, handleToggleDeleteConfirmation, showDeleteConfirmation],
  )

  useDidMount(() => {
    register('id')
    register('duration')
    register('currencyId')
    register('currencyName')
    register('price')
    register('tags')
  })

  useEffect(() => {
    handleGetCurrencyData()
  }, [handleGetCurrencyData])

  return (
    <StyledContainer>
      {handleRenderModalLoading}
      {handleRenderModalDelete}
      <StyledSection>
        <GiftShopSettingPackageFormTags
          form={form}
          tags={tags}
          disabled={tagsDisabled}
        />
        <GiftShopSettingPackageFormPrice
          stateInputPrice={stateInputPrice}
          currencyName={currencyName}
          form={form}
          currencyData={currencyData}
        />
        <GiftShopSettingPackageFormDuration form={form} />
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            gap: convertUnit(20),
          }}>
          {onDelete && (
            <ButtonIcon
              iconColor="primary_5"
              backgroundColor="white_3"
              style={{
                marginTop: 20,
                boxShadow: 'none',
              }}
              onClick={() => setShowDeleteConfirmation(true)}
              iconType="trash-ol"
            />
          )}
          <Button
            style={{
              marginTop: 20,
              width: '100%',
            }}
            label={translate('giftShop:settingPackageSubmit')}
            onClick={handleSubmit}
            disabled={loading}
          />
        </div>
      </StyledSection>
      <StyledSection>
        <Paragraph fontWeight="bold" color="gray_5">
          {translate('global:description')}:
        </Paragraph>
        <StyledParagraphList
          style={{
            color: theme.gray_5,
          }}>
          {descriptionPoints}
        </StyledParagraphList>
      </StyledSection>
    </StyledContainer>
  )
}
